
import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import Page from '@root/common/base/Page';
import Meta from '@root/common/utils/Meta';
import UniversalHeadlinesService from '@root/modules/headlines/services/UniversalHeadlines.service';
import HeadlineList from '@root/common/components/base/headline/HeadlineList.vue';
import { Headline } from '@root/common/types/fragment';
import PageHeader from '@root/common/components/base/PageHeader.vue';
import BookmarksPageSkeletonLoader from '@root/modules/customer/components/BookmarksPageSkeletonLoader.vue';
import ErrorPage from '@root/modules/customer/components/ErrorPage.vue';
import PianoCustomerStateHandler from '@piano/components/PianoCustomerStateHandler.vue';
import { GetReadingHistory } from '@root/modules/customer/types/customer';
import CustomerDataService from '@root/modules/customer/services/CustomerData.service';
import CustomerNavigation from '@root/modules/customer/components/CustomerNavigation.vue';
import getPageAdsSettings from '@root/modules/customer/utils/getPageAdsSettings';

type GroupedHistoryData = {
  [date: string]: {
    ids: number[];
    headlines: any;
  };
};

export default Page.extend({
  components: {
    HeadlineList,
    PageHeader,
    BookmarksPageSkeletonLoader,
    ErrorPage,
    PianoCustomerStateHandler,
    CustomerNavigation,
  },
  data() {
    return {
      isLoading: true,
      readingHistory: null as GetReadingHistory['data']['data'] | null,
      readingHistoryByDate: null as GroupedHistoryData | null,
      headlineSettings: { icons: false, contentSeparator: true, customerDataType: 'reading-history' },
      customerDataService: null as CustomerDataService | null,
      universalHeadlinesService: null as UniversalHeadlinesService | null,
      pager: {
        limit: 50,
        offset: 0,
        count: 0,
      },
      fetchingHeadlines: false,
    };
  },
  computed: {
    customerToken(): string {
      return this.$store.state.piano.token;
    },
    customerHasAccess() {
      return this.$store.state.piano.access.channelAccess;
    },
    isPianoScriptInited() {
      return this.$store.state.piano.isScriptInited;
    },
  },
  methods: {
    async getPageData() {
      const limit = this.pager.limit;
      const offset = this.pager.offset;

      this.fetchingHeadlines = true;

      try {
        await this.getReadingHistory(limit, offset);
      } finally {
        this.fetchingHeadlines = false;
      }
    },

    async getReadingHistory(limit?: number, offset?: number) {
      const response =
        (await this.customerDataService?.getReadingHistory({
          limit: limit ?? this.pager.limit,
          offset: offset ?? this.pager.offset,
        })) || null;

      if (response?.data.items?.length) {
        if (this.readingHistory?.items) {
          this.readingHistory.items = this.readingHistory.items.concat(response?.data.items);
        } else {
          this.readingHistory = response?.data || null;
        }

        const groupedHistoryData: GroupedHistoryData = {};

        this.readingHistory.items.forEach((item) => {
          const date = dayjs(item.createdAt).format('YYYY-MM-DD');

          if (!groupedHistoryData[date]) {
            groupedHistoryData[date] = { ids: [], headlines: [] };
          }

          groupedHistoryData[date].ids.push(item.articleId);
        });

        this.readingHistoryByDate = groupedHistoryData;
        this.isLoading = false;

        const fetchHeadlinesTasks = Object.keys(groupedHistoryData).map(async (date) => {
          const headlinesIds = Array.from(groupedHistoryData[date].ids);
          const headlines = await this.getHeadlines(headlinesIds);

          if (this.readingHistoryByDate?.[date] && headlines?.items?.length) {
            this.$set(this.readingHistoryByDate[date], 'headlines', headlines);
          } else if (this.readingHistoryByDate?.[date]) {
            this.$delete(this.readingHistoryByDate, date);
          }
        });

        await Promise.all(fetchHeadlinesTasks);
        return;
      }

      if (!this.readingHistory?.items?.length) {
        this.readingHistory = response?.data || null;
        this.readingHistoryByDate = null;
      }

      this.isLoading = false;
    },

    async getHeadlines(articleIds: number[]) {
      if (!this.readingHistory?.items.length) {
        return;
      }

      const headlines = await this.universalHeadlinesService?.fetch({
        id: articleIds,
        offset: 0,
        limit: 50,
        getCount: true,
      });

      if (headlines?.items) {
        const sortedHeadlines = this.resort(headlines.items, articleIds);
        headlines.items = sortedHeadlines;
        return headlines;
      }
    },

    deleteReadingHistoryEntry(id: number) {
      const entryIndex = this.readingHistory?.items.findIndex((readingHistoryEntry) => readingHistoryEntry.articleId === id);

      if (entryIndex !== undefined && entryIndex >= 0) {
        const entry = this.readingHistory!.items.splice(entryIndex as number, 1)[0];

        if (this.readingHistoryByDate) {
          Object.keys(this.readingHistoryByDate).forEach((date) => {
            if (this.readingHistoryByDate?.[date]?.headlines?.items) {
              this.readingHistoryByDate[date].headlines.items = this.readingHistoryByDate[date].headlines.items.filter(
                (item: Headline) => item.id !== entry.articleId
              );

              if (!this.readingHistoryByDate[date].headlines.items.length) {
                this.$delete(this.readingHistoryByDate, date);
              }
            }
          });
        }

        this.customerDataService?.deleteReadingHistoryEntry({
          id: entry!.id,
        });
      }
    },
    resort(items: Headline[], ids: number[]) {
      if (!ids.length) {
        return items;
      }

      const positions = new Map(ids.map((itemId, index) => [itemId, index]));

      return items.sort((a: any, b: any) => (positions.get(a.id) ?? 0) - (positions.get(b.id) ?? 0));
    },
    increaseHeadlinesOffset() {
      this.pager.offset += 50;
    },
    convertDate(date: string | number) {
      const { locale } = this.$channelConfig('settings');

      switch (locale) {
        case 'ru_LT':
          return dayjs(date).locale('ru').format('dddd, D MMMM YYYY');

        default:
          return `${dayjs(date).format('dddd').toUpperCase()}, ${dayjs(date).format('YYYY-MM-DD')}`;
      }
    },
  },
  watch: {
    async customerToken() {
      this.customerDataService?.setCustomerToken(this.customerToken);

      if (this.customerToken) {
        await this.getPageData();
      } else {
        this.isLoading = false;
      }
    },
    async 'pager.offset'() {
      if (this.customerToken) {
        await this.getPageData();
      }
    },
  },
  async created() {
    this.setPageAdsSettings(getPageAdsSettings({ $channelConfig: this.$channelConfig }));

    this.customerDataService = new CustomerDataService();
    this.universalHeadlinesService = new UniversalHeadlinesService();

    if (this.customerToken) {
      this.customerDataService.setCustomerToken(this.customerToken);
      await this.getPageData();
    } else {
      this.isLoading = false;
    }
  },
  mounted() {
    this.restoreScroll();
    this.$eventHandler.on('delete-reading-history-entry', this.deleteReadingHistoryEntry);
  },
  beforeRouteLeave(to, from, next) {
    this.resetPageAdsSettings();
    this.resetPageHeaderBottomNavigation();

    next();
  },
  beforeDestroy() {
    this.$eventHandler.off('delete-reading-history-entry', this.deleteReadingHistoryEntry);
  },
  head() {
    const meta = new Meta(this.$channelConfig('meta').base, this.$route.path);

    meta.title = meta.ogTitle = this.$t('history.meta.title') as string;
    meta.description = meta.ogDescription = this.$t('history.meta.description') as string;

    return {
      title: meta.title,
      meta: meta.data,
      link: meta.link,
    };
  },
});
